import { useState, useRef } from 'react';
import { useClientLayoutEffect } from './useClientLayoutEffect.js';

function useThrottle(value, delay, { leading = true, trailing = true } = {}) {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastRun = useRef(Date.now());
  const isFirstCall = useRef(true);
  const timeoutRef = useRef();
  const valueRef = useRef(value);
  useClientLayoutEffect(() => {
    valueRef.current = value;
  }, [
    value
  ]);
  useClientLayoutEffect(() => {
    isFirstCall.current = true;
    lastRun.current = 0;
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    leading,
    trailing,
    delay
  ]);
  useClientLayoutEffect(() => {
    const now = Date.now();
    const timeSinceLastRun = now - lastRun.current;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = void 0;
    }
    const shouldTriggerLeading = leading && (isFirstCall.current || timeSinceLastRun >= delay);
    const shouldTriggerThrottled = !isFirstCall.current && timeSinceLastRun >= delay;
    if (shouldTriggerLeading || shouldTriggerThrottled) {
      isFirstCall.current = false;
      setThrottledValue(valueRef.current);
      lastRun.current = now;
    } else if (trailing && !isFirstCall.current) {
      setThrottledValue(valueRef.current);
      lastRun.current = now;
    } else if (trailing) {
      timeoutRef.current = setTimeout(() => {
        setThrottledValue(valueRef.current);
        lastRun.current = Date.now();
      }, delay - timeSinceLastRun);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    value,
    delay,
    leading,
    trailing
  ]);
  return throttledValue;
}

export { useThrottle };
//# sourceMappingURL=useThrottle.js.map
